// ============================================================================
// « ÉTIQUETTES » — onglet 2, l'impression de masse (maquette `lbTabPrint`, relevé §4).
//
// Trois temps numérotés : ① chercher & cocher · ② le panier · ③ imprimer.
//
// ⚠️ LE PANIER RAISONNE EN DÉCLINAISONS, PAS EN MODÈLES. On colle une étiquette sur un article
// physique : elle porte SA taille, SA couleur, SON code-barres. Un panier de modèles ne saurait pas
// combien d'étiquettes tirer, ni lesquelles.
//
// ⚠️ LES VALEURS IMPRIMÉES SONT RELUES AU MOMENT D'IMPRIMER, jamais reprises de la liste. Entre le
// moment où on coche et celui où on lance, un prix a pu changer — et c'est le papier qui ment
// ensuite, en rayon, pendant des semaines.
//
// ⚠️ « UNE PLANCHE GÂCHÉE NE SE RÉIMPRIME PAS » : c'est la phrase de la maquette, et c'est pourquoi
// le récapitulatif existe. Les compteurs (étiquettes, feuilles) sont mesurés sur ce qui SERA tiré,
// jamais estimés.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mediaLabel, templateBarcodeQuality, type LabelFieldKey, type LabelTemplate } from '@orgazone/core-catalog';
import { alert, Button, EmptyState, Field, Icon, Modal, Picker, Popover, Segmented, Trans } from '@orgazone/ui-web';
import { api, type ArticleModel, type ArticleVariant, type LabelSampleRow } from '../api';
import { LabelPlate, labelValuesOf } from './etiquettes-render';
import { buildPrintDocument, countSheets, openPrintDocument, type PrintRow } from './etiquettes-print';
import { fieldLabel } from '../vocabulary';
import { useStoreScope } from '../store-context';
import { lastLabelTemplate, rememberLabelTemplate } from '../label-last-template';
import { key, t } from '../i18n';

/** Le plafond de résultats — au-delà, on affine les filtres plutôt que de faire défiler. */
const RESULTS_MAX = 60;
/** Ce que le récapitulatif affiche avant de dire « et n autres ». */
const RECAP_SHOWN = 40;

/** Les trois sources de la maquette. ⚠️ Deux ne sont pas branchées, et l'écran le DIT. */
const SOURCES = [
  { id: 'cat', label: key('Catalogue') },
  { id: 'bl', label: key('Bon de livraison') },
  { id: 'order', label: key('Commande') },
] as const;
type Source = (typeof SOURCES)[number]['id'];

/** Un article du panier, avec ses déclinaisons. */
interface CartEntry {
  model: ArticleModel;
  variants: ArticleVariant[];
  open: boolean;
}

/** Les deux modes de quantité — ⚠️ une saisie par déclinaison les SURCHARGE tous les deux. */
type QtyMode = 'stock' | 'one';

/**
 * Les quatre référentiels par lesquels on filtre — la CLÉ du filtre, celle que l'API reçoit.
 * ⚠️ La MARQUE et non le fournisseur : c'est ce que portent les articles (le fournisseur est un groupe, vide
 *    sur presque tout le catalogue), et c'est ce qu'on cherche en rayon.
 */
type FacetKey = 'marq' | 'fam' | 'sais' | 'sect';

export interface TunnelProps {
  /** Les gabarits visibles du magasin — le tunnel n'en fabrique aucun. */
  templates: LabelTemplate[];
}

export function PrintTunnel({ templates }: TunnelProps) {
  const [source, setSource] = useState<Source>('cat');

  // ── ① Chercher & cocher ────────────────────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [facets, setFacets] = useState<Record<string, string[]>>({});
  const [picked, setPicked] = useState<Record<FacetKey, string>>({ marq: '', fam: '', sais: '', sect: '' });
  const [results, setResults] = useState<ArticleModel[]>([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  // ⚠️ FILTRE SERVEUR : « en stock » qualifie un MODÈLE (une chemise dont seul le M reste EST en
  //    stock), et se juge donc après regroupement, là où le navigateur ne voit que 60 lignes.
  const [inStock, setInStock] = useState(false);

  // ── ② Le panier ────────────────────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartEntry[]>([]);
  const [qtyMode, setQtyMode] = useState<QtyMode>('stock');
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  // ── ③ Imprimer ─────────────────────────────────────────────────────────────────────────────────
  const gln = useStoreScope().view?.active?.gln ?? null;
  // Rien de choisi : le dernier gabarit imprimé sur ce poste, s'il est encore proposé, sinon le premier.
  const [templateKey, setTemplateKey] = useState<string>('');
  const [usedOnFirstSheet, setUsedOnFirstSheet] = useState(0);
  const [recapOpen, setRecapOpen] = useState(false);
  const [printing, setPrinting] = useState(false);

  // ⚠️ LE GABARIT CHOISI PEUT AVOIR ÉTÉ MASQUÉ dans l'autre onglet : sans ce repli, « Lancer
  //    l'impression » viserait un gabarit absent et ne ferait rien, sans rien dire.
  const template = templates.find((tpl) => tpl.key === templateKey)
    ?? templates.find((tpl) => tpl.key === lastLabelTemplate(gln, templates)) ?? templates[0] ?? null;

  useEffect(() => {
    api.articleFacets()
      .then((f) => setFacets(Object.fromEntries(Object.entries(f.values).map(([k, v]) => [k, v.map((x) => x.value)]))))
      // ⚠️ BEST-EFFORT JUSTIFIÉ : sans référentiels, la recherche texte fonctionne toujours. Priver
      //    l'écran entier parce que des listes déroulantes manquent serait disproportionné.
      .catch(() => setFacets({}));
  }, []);

  // ⚠️ UNE RÉPONSE DÉPASSÉE NE RÉÉCRIT PAS LES RÉSULTATS : chaque frappe lance une recherche, et la réponse à « pu » peut
  //    revenir APRÈS celle à « pull » — la liste montrerait les articles d'une recherche que le champ ne dit plus, et on
  //    cocherait pour l'impression des articles qu'on n'a pas cherchés.
  const searchSeq = useRef(0);
  const search = useCallback(async () => {
    const mine = ++searchSeq.current;
    setSearching(true);
    setSearchError(null);
    try {
      const page = await api.articles({
        page: 0, size: RESULTS_MAX, sort: 'designation', dir: 'asc', q: query,
        ...(picked.marq ? { marq: picked.marq } : {}),
        ...(picked.fam ? { fam: picked.fam } : {}),
        ...(picked.sais ? { sais: picked.sais } : {}),
        ...(picked.sect ? { sect: picked.sect } : {}),
        ...(inStock ? { inStock: true } : {}),
      });
      if (mine !== searchSeq.current) return;
      setResults(page.rows);
      setTotal(page.total);
    } catch (e) {
      if (mine !== searchSeq.current) return;
      // ⚠️ LE MOTIF RÉEL : une liste vide sans explication se lit « ce magasin n'a pas d'articles ».
      setSearchError(e instanceof Error ? e.message : String(e));
      setResults([]);
      setTotal(0);
    } finally {
      if (mine === searchSeq.current) setSearching(false);
    }
  }, [query, picked, inStock]);

  useEffect(() => { void search(); }, [search]);

  const addToCart = useCallback(async (models: ArticleModel[]) => {
    const fresh = models.filter((m) => !cart.some((c) => c.model.modelKey === m.modelKey));
    if (fresh.length === 0) return;
    try {
      const sets = await Promise.all(fresh.map((m) => api.articleVariants(m.modelKey)));
      const entries: CartEntry[] = fresh.map((m, i) => ({
        model: m,
        // ⚠️ SANS CODE-BARRES, PAS D'ÉTIQUETTE : on écarte ces déclinaisons ici plutôt que de les
        //    montrer cochables pour ne rien imprimer ensuite.
        variants: (sets[i]?.rows ?? []).filter((v) => !!v.gencode),
        open: true,
      }));
      setCart((c) => [...c, ...entries]);
      setChecked(new Set());
      const emptyOnes = entries.filter((e) => e.variants.length === 0).map((e) => e.model.name);
      if (emptyOnes.length > 0) {
        // ⚠️ ON LE DIT PLUTÔT QUE DE LES AJOUTER MUETS : une ligne de panier à zéro étiquette se lit
        //    comme un bug, alors que c'est un article sans code-barres — ce qui se corrige.
        void alert({
          title: t('Certains articles n’ont aucun code-barres'),
          message: t('{v} — sans code-barres, aucune étiquette ne peut être tirée pour ces articles.', { v: emptyOnes.join(', ') }),
          tone: 'info',
        });
      }
    } catch (e) {
      void alert({ title: t('Les déclinaisons n’ont pas pu être lues'), message: e instanceof Error ? e.message : String(e) });
    }
  }, [cart]);

  /**
   * La quantité d'une déclinaison.
   * ⚠️ LA SAISIE LIBRE SURCHARGE LE MODE, et c'est la règle que la maquette écrit à l'écran : celui
   * qui a tapé un nombre l'a voulu, changer de mode ne doit pas l'effacer.
   * ⚠️ Un stock inconnu (`null`) vaut ZÉRO en mode « selon le stock » : imprimer « 1 par défaut »
   * ferait des étiquettes pour des articles dont on ne sait pas s'ils sont là.
   */
  const qtyOf = useCallback((v: ArticleVariant): number => {
    const key = v.gencode ?? '';
    if (excluded.has(key)) return 0;
    const forced = overrides[key];
    if (forced !== undefined) return Math.max(0, forced);
    return qtyMode === 'one' ? 1 : Math.max(0, v.stockQty ?? 0);
  }, [excluded, overrides, qtyMode]);

  /** Ce qui sera RÉELLEMENT tiré — le seul chiffre que l'écran a le droit d'annoncer. */
  const lines = useMemo(
    () => cart.flatMap((e) => e.variants.map((v) => ({ entry: e, variant: v, qty: qtyOf(v) })).filter((l) => l.qty > 0)),
    [cart, qtyOf],
  );
  const labelCount = lines.reduce((n, l) => n + l.qty, 0);
  const sheetCount = template ? countSheets(template, labelCount, usedOnFirstSheet) : 0;

  const launchPrint = useCallback(async () => {
    if (!template || lines.length === 0) return;
    setPrinting(true);
    try {
      // ⚠️ ON RELIT LES VALEURS MAINTENANT, jamais celles de la liste : entre le clic sur une case et
      //    le lancement, un prix a pu changer — et c'est le papier qui mentirait ensuite, en rayon.
      const data = await api.labelData(lines.map((l) => l.variant.gencode!));
      const byCode = new Map<string, LabelSampleRow>(data.rows.map((r) => [r.gencode, r]));
      const rows: PrintRow[] = [];
      const missing: string[] = [];
      for (const l of lines) {
        const row = byCode.get(l.variant.gencode!);
        if (!row) { missing.push(l.variant.gencode!); continue; }
        rows.push({
          copies: l.qty,
          values: labelValuesOf(row),
        });
      }
      if (rows.length === 0) {
        void alert({ title: t('Rien à imprimer'), message: t('Aucune des déclinaisons du panier n’a pu être relue dans le catalogue.') });
        return;
      }
      if (missing.length > 0) {
        // ⚠️ ON N'IMPRIME PAS EN SILENCE CE QU'ON A PU LIRE : quelques articles repartiraient en
        //    rayon sans étiquette, et personne ne le saurait avant l'inventaire.
        void alert({
          title: t('{n} déclinaison introuvable', { n: missing.length }),
          message: t('Ces codes-barres n’existent plus au catalogue et ne seront pas imprimés : {v}', { v: missing.slice(0, 10).join(', ') }),
          tone: 'info',
        });
      }
      const outcome = openPrintDocument(buildPrintDocument(template, rows, { usedOnFirstSheet }));
      if (!outcome.ok) {
        // ⚠️ UN ÉCHEC D'OUVERTURE SE DIT (§23.0) : sinon l'opérateur attend devant une imprimante
        //    silencieuse en croyant que le papier arrive.
        void alert({ title: t('La page d’impression n’a pas pu s’ouvrir'), message: outcome.reason });
      } else {
        rememberLabelTemplate(gln, template.key);
      }
    } catch (e) {
      void alert({ title: t('L’impression n’a pas pu être préparée'), message: e instanceof Error ? e.message : String(e) });
    } finally {
      setPrinting(false);
    }
  }, [template, lines, usedOnFirstSheet, gln]);

  const filtered = !!(query || picked.marq || picked.fam || picked.sais || picked.sect || inStock);
  const allShownChecked = results.length > 0 && results.every((m) => checked.has(m.modelKey));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SourceBar value={source} onChange={setSource} />

      {source !== 'cat' ? (
        /* ⚠️ LE PLAN RESTE VISIBLE ET DIT CE QUI MANQUE. Masquer ces deux sources ferait croire que
           les étiquettes ne se tirent que du catalogue ; les laisser muettes ferait croire qu'elles
           marchent (§23.0). */
        <EmptyState
          icon={source === 'bl' ? 'inbound' : 'file'}
          title={source === 'bl' ? t('Le tirage depuis un bon de livraison n’est pas encore branché') : t('Le tirage depuis une commande n’est pas encore branché')}
          note={t('Les articles d’un document rejoindront le panier exactement comme ceux du catalogue. En attendant, retrouvez-les par la recherche « Catalogue ».')}
        />
      ) : (
        <>
          {/* ⚠️ ① ET ② CÔTE À CÔTE, ③ EN BANDE DESSOUS — c'est la disposition de la maquette, et elle
              a une raison : on coche à gauche et on voit le panier grossir à droite, dans le même
              regard. Empilés, chaque ajout demanderait de faire défiler pour vérifier son effet. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 16, alignItems: 'start' }}>

            {/* ── ① CHERCHER & COCHER ───────────────────────────────────────────────────────── */}
            <section style={CARD}>
              <StepTitle mark="①" title={t('Chercher & cocher')} />

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Field
                  label={t('Rechercher')}
                  labelStyle="dense"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('Désignation, code, code-barres, marque…')}
                  style={{ minWidth: 200, flex: 1 }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8 }}>
                <FacetPicker facet="marq" label={fieldLabel('brand')} all={t('Toutes les marques')} value={picked.marq} options={facets.marq ?? []} onChange={(v) => setPicked((p) => ({ ...p, marq: v }))} />
                <FacetPicker facet="fam" label={fieldLabel('family')} all={t('Toutes les familles')} value={picked.fam} options={facets.fam ?? []} onChange={(v) => setPicked((p) => ({ ...p, fam: v }))} />
                <FacetPicker facet="sais" label={fieldLabel('season')} all={t('Toutes les saisons')} value={picked.sais} options={facets.sais ?? []} onChange={(v) => setPicked((p) => ({ ...p, sais: v }))} />
                <FacetPicker facet="sect" label={fieldLabel('sector')} all={t('Tous les secteurs')} value={picked.sect} options={facets.sect ?? []} onChange={(v) => setPicked((p) => ({ ...p, sect: v }))} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* ⚠️ FILTRE SERVEUR, jamais un tri du navigateur : appliqué sur la page reçue, il ne
                    cacherait que des lignes parmi les 60 premières et le compte annoncé cesserait de
                    décrire la liste. */}
                <Button size="sm" aria-pressed={inStock} variant={inStock ? 'primary' : 'ghost'} icon={inStock ? 'check' : 'box'} onClick={() => setInStock((v) => !v)}>
                  
                  {t('En stock uniquement')}
                </Button>
                {filtered && (
                  <Button size="sm" icon="x" onClick={() => { setQuery(''); setPicked({ marq: '', fam: '', sais: '', sect: '' }); setInStock(false); }}>
                    
                    {t('Effacer')}
                  </Button>
                )}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
                  {searching ? t('Recherche…') : t('{n} article', { n: total })}
                </span>
              </div>

              {results.length > 0 && (
                /* Cocher soixante lignes une à une n'est pas un geste : la maquette offre la bascule. */
                <Button
                  size="sm"
                  icon={allShownChecked ? 'check' : 'grid'}
                  onClick={() => setChecked((s) => {
                    if (allShownChecked) return new Set();
                    const next = new Set(s);
                    for (const m of results) next.add(m.modelKey);
                    return next;
                  })}
                >
                  {allShownChecked ? t('Tout décocher') : t('Sélectionner les {n} affichés', { n: results.length })}
                </Button>
              )}

              {searchError ? (
                <EmptyState icon="alert" title={t('La recherche a échoué')} note={searchError} compact />
              ) : results.length === 0 && !searching ? (
                <EmptyState icon="search" title={t('Aucun article')} note={t('Aucun article ne correspond à ces filtres croisés.')} compact />
              ) : (
                <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {results.map((m) => (
                    <div key={m.modelKey} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderBottom: '1px solid var(--ink-100)' }}>
                      <input
                        type="checkbox"
                        aria-label={t('Sélectionner {name}', { name: m.name })}
                        checked={checked.has(m.modelKey)}
                        onChange={(e) => setChecked((s) => {
                          const next = new Set(s);
                          if (e.target.checked) next.add(m.modelKey); else next.delete(m.modelKey);
                          return next;
                        })}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={ELLIPSIS_BOLD}>{m.name}</div>
                        <div style={ELLIPSIS_MUTED}>{[m.articleCode, m.family, m.brand].filter(Boolean).join(' · ')}</div>
                      </div>
                      {/* ⚠️ `null` = AUCUNE ligne de stock, distinct d'un stock mesuré à zéro. */}
                      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
                        {m.stockQty === null ? '—' : m.stockQty}
                      </span>
                      <button
                        type="button"
                        aria-label={t('Ajouter {name} au panier', { name: m.name })}
                        title={t('Ajouter directement au panier')}
                        onClick={() => { void addToCart([m]); }}
                        style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', cursor: 'pointer', padding: '2px 6px', color: 'var(--ink-600)' }}
                      >
                        <Icon name="plus" size={12} />
                      </button>
                    </div>
                  ))}
                  {total > RESULTS_MAX && (
                    <div style={{ padding: '8px 4px', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
                      {t('Affinez les filtres — seuls les {n} premiers sont affichés.', { n: RESULTS_MAX })}</div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 140, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('La sélection se cumule entre recherches.')}</span>
                <Button
                  variant="primary"
                  disabled={checked.size === 0}
                  onClick={() => { void addToCart(results.filter((m) => checked.has(m.modelKey))); }}
                >
                  {t('Ajouter au panier ({size})', { size: checked.size })}</Button>
              </div>
            </section>

            {/* ── ② LE PANIER ───────────────────────────────────────────────────────────────── */}
            <section style={CARD}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StepTitle mark="②" title={t('Le panier')} />
                <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
                  — {cart.length === 0 ? t('aucun article') : t('{n} article', { n: cart.length })}
                </span>
                <span style={{ flex: 1 }} />
                {cart.length > 0 && (
                  <>
                    <button
                      type="button"
                      aria-label={cart.every((e) => e.open) ? t('Tout replier') : t('Tout déplier')}
                      title={cart.every((e) => e.open) ? t('Tout replier') : t('Tout déplier')}
                      onClick={() => setCart((c) => { const allOpen = c.every((x) => x.open); return c.map((e) => ({ ...e, open: !allOpen })); })}
                      style={ICON_BUTTON}
                    >
                      <Icon name={cart.every((e) => e.open) ? 'chevronU' : 'chevronD'} size={13} />
                    </button>
                    <button type="button" aria-label={t('Vider le panier')} title={t('Vider le panier')}
                      onClick={() => { setCart([]); setOverrides({}); setExcluded(new Set()); }} style={ICON_BUTTON}>
                      <Icon name="trash" size={13} />
                    </button>
                  </>
                )}
              </div>

              {cart.length === 0 ? (
                <EmptyState
                  icon="bag"
                  title={t('Le panier est vide')}
                  note={t('Filtrez à gauche, cochez des articles, « Ajouter au panier ». Le récapitulatif et l’impression apparaîtront en dessous.')}
                  compact
                />
              ) : (
                <>
                  <div>
                    <Segmented
                      label={t('Quantité')}
                      options={[{ id: 'stock', label: t('Selon le stock') }, { id: 'one', label: t('1 par déclinaison') }]}
                      value={qtyMode}
                      onChange={(v) => setQtyMode(v as QtyMode)}
                      size="sm"
                    />
                    <div style={{ marginTop: 5, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('La saisie libre par déclinaison surcharge le mode.')}</div>
                  </div>

                  <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cart.map((entry) => (
                      <CartRow
                        key={entry.model.modelKey}
                        entry={entry}
                        qtyOf={qtyOf}
                        excluded={excluded}
                        onToggle={() => setCart((c) => c.map((e) => (e.model.modelKey === entry.model.modelKey ? { ...e, open: !e.open } : e)))}
                        onRemove={() => setCart((c) => c.filter((e) => e.model.modelKey !== entry.model.modelKey))}
                        onExclude={(gencode, on) => setExcluded((s) => {
                          const next = new Set(s);
                          if (on) next.delete(gencode); else next.add(gencode);
                          return next;
                        })}
                        onQty={(gencode, n) => setOverrides((o) => ({ ...o, [gencode]: n }))}
                      />
                    ))}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, paddingTop: 4, borderTop: '1px solid var(--ink-100)' }}>
                    <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Total en direct')}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>
                      {t('{n} étiquette', { n: labelCount })}
                    </span>
                  </div>
                </>
              )}
            </section>
          </div>

          {/* ── ③ IMPRIMER — une BANDE, pas une colonne ────────────────────────────────────────
              ⚠️ Elle reste visible même panier vide : c'est là qu'on choisit le gabarit, et le
              choisir AVANT de remplir le panier est l'ordre naturel quand on sait ce qu'on tire. */}
          <section style={{ ...CARD, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
            <StepTitle mark="③" title={t('Imprimer')} />

            {!template ? (
              <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
                
                {t('Aucun gabarit — créez une étiquette dans l’onglet « Gabarits ».')}
              </span>
            ) : (
              <>
                <TemplatePicker templates={templates} current={template} onPick={setTemplateKey} />

                {template.kind === 'a4' && <UsedOnFirstSheetField value={usedOnFirstSheet} onChange={setUsedOnFirstSheet} />}

                <span style={{ flex: 1 }} />

                <BarcodeWidthWarning template={template} />

                <Button size="sm" disabled={lines.length === 0} onClick={() => setRecapOpen(true)}>
                  {t('Vérifier le détail ({n})', { n: lines.length })}</Button>

                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('{n} étiquette', { n: labelCount })}</span>
                  {template.kind === 'a4' && (
                    <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('{n} feuille A4', { n: sheetCount })}</span>
                  )}
                </span>

                <Button variant="primary" icon="printer" disabled={labelCount === 0 || printing} onClick={() => { void launchPrint(); }}>
                  {printing ? t('Préparation…') : t('Lancer l’impression')}
                </Button>
              </>
            )}
          </section>
        </>
      )}

      {recapOpen && template && (
        <Modal open title={t('Récapitulatif')} subtitle={t('{n} déclinaison concernée', { n: lines.length })} icon="printer" onClose={() => setRecapOpen(false)} width="lg"
          meta={
            <span style={{ display: 'flex', gap: 18 }}>
              <RecapCount label={t('Étiquettes')} value={String(labelCount)} />
              {template.kind === 'a4' && <RecapCount label={t('Feuilles A4')} value={String(sheetCount)} />}
            </span>
          }
          footer={
            <span style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-dense)', color: 'var(--amber-fg)' }}>
                <Trans k='Vérifiez le total avant de lancer : <1>une planche gâchée ne se réimprime pas.</1>' parts={[(x) => <strong>{x}</strong>]} />
              </span>
              <Button onClick={() => setRecapOpen(false)}>{t('Fermer')}</Button>
              {/* ⚠️ ON PEUT LANCER D'ICI : c'est l'écran où l'on vient de vérifier. Obliger à fermer
                  pour retrouver le bouton ferait perdre de vue ce qu'on venait de contrôler. */}
              <Button variant="primary" icon="printer" disabled={labelCount === 0 || printing}
                onClick={() => { setRecapOpen(false); void launchPrint(); }}>
                
                {t('Lancer l’impression')}
              </Button>
            </span>
          }
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-dense)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--ink-500)' }}>
                  <th style={TH}>{t('Article')}</th><th style={TH}>{fieldLabel('color')}</th><th style={TH}>{fieldLabel('size')}</th>
                  <th style={TH}>{fieldLabel('gencode')}</th><th style={{ ...TH, textAlign: 'right' }}>{t('Stock')}</th>
                  <th style={{ ...TH, textAlign: 'right' }}>{t('Étiq.')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.slice(0, RECAP_SHOWN).map((l) => (
                  <tr key={l.variant.gencode} style={{ borderTop: '1px solid var(--ink-100)' }}>
                    <td style={TD}>
                      <div style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{l.entry.model.name}</div>
                      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{l.entry.model.articleCode ?? ''}</div>
                    </td>
                    <td style={TD}>{l.variant.color ?? '—'}</td>
                    <td style={TD}>{l.variant.size ?? '—'}</td>
                    <td style={{ ...TD, fontFamily: 'var(--font-mono)' }}>{l.variant.gencode}</td>
                    {/* ⚠️ UN STOCK À ZÉRO SE DISTINGUE À L'ŒIL : on imprime alors une étiquette pour
                        un article que le rayon n'a pas — c'est parfois voulu, jamais anodin. */}
                    <td style={{ ...TD, textAlign: 'right', color: (l.variant.stockQty ?? 0) <= 0 ? 'var(--amber-fg)' : 'var(--ink-800)' }}>
                      {l.variant.stockQty ?? '—'}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{l.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lines.length > RECAP_SHOWN && (
            // ⚠️ ON DIT CE QU'ON NE MONTRE PAS : une liste tronquée en silence ferait vérifier un
            //    total sur une partie des lignes.
            <div style={{ marginTop: 10, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
              {t('+ {n} déclinaison non affichée — toutes seront imprimées.', { n: lines.length - RECAP_SHOWN })}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

const ELLIPSIS_BOLD = { fontSize: 'var(--t-body)', fontWeight: 600 as const, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const };
const ELLIPSIS_MUTED = { fontSize: 'var(--t-meta)', color: 'var(--ink-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const };
const ICON_BUTTON = { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-500)', padding: 2 };

/** Un compteur de l'en-tête du récapitulatif — l'intitulé au-dessus, le chiffre en gros. */
function RecapCount({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontSize: 'var(--t-micro)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</span>
      <span style={{ fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{value}</span>
    </span>
  );
}

/**
 * LE SÉLECTEUR DE GABARIT — une vignette, pas une liste de noms.
 *
 * ⚠️ ON RECONNAÎT UN GABARIT À SA FORME, pas à son nom. « Étiquette portant » et « Étiquette prix »
 * ne disent rien de ce qui sortira de l'imprimante ; la disposition, si — et c'est elle qu'on vérifie
 * avant de lancer une planche. D'où le MÊME rendu que l'éditeur, en miniature.
 */
function TemplatePicker({ templates, current, onPick }: {
  templates: LabelTemplate[]; current: LabelTemplate; onPick: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const vignette = (tpl: LabelTemplate, max: number) => (
    <LabelPlate template={tpl} values={neutralPlateValues()} scale={Math.min(max / tpl.widthMm, (max * 0.42) / tpl.heightMm)} />
  );
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      id="etq-gabarits"
      label={t('Choisir le gabarit d’étiquette')}
      trigger={(api) => (
        <button
          type="button"
          {...api}
          ref={api.ref}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '7px 11px', cursor: 'pointer',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', textAlign: 'left',
          }}
        >
          {vignette(current, 74)}
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{current.name}</span>
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{mediaLabel(current)}</span>
          </span>
          <Icon name="chevronD" size={13} />
        </button>
      )}
    >
      {({ close }) => (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 8, padding: 10, maxWidth: 620 }}>
          {templates.map((tpl) => (
            <button
              key={tpl.key}
              type="button"
              onClick={() => { onPick(tpl.key); close(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                border: `1px solid ${tpl.key === current.key ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                borderRadius: 'var(--r-xs)', background: tpl.key === current.key ? 'var(--teal-100)' : 'var(--paper)',
              }}
            >
              {vignette(tpl, 64)}
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ ...ELLIPSIS_BOLD, fontSize: 'var(--t-dense)' }}>{tpl.name}</span>
                <span style={ELLIPSIS_MUTED}>{mediaLabel(tpl)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

const CARD = {
  display: 'flex' as const, flexDirection: 'column' as const, gap: 12, padding: 16,
  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', minWidth: 0,
};
const TH = { padding: '6px 8px', fontWeight: 600 as const };
const TD = { padding: '6px 8px', color: 'var(--ink-800)' };

/**
 * Les valeurs neutres d'une plaque — la vignette du sélecteur de gabarit, et l'aperçu d'une modale
 * tant que le catalogue n'a pas été relu.
 * ⚠️ CE N'EST PAS UNE DONNÉE, C'EST UNE FORME : la vignette sert à reconnaître une DISPOSITION d'un
 * coup d'œil, pas à lire un article. Les libellés nomment donc les champs — avec le mot du magasin,
 * puisque c'est lui que l'opérateur reconnaît — et personne ne peut les prendre pour un vrai
 * article, ce qu'un « Chemise Oxford · 69,99 € » inventé ferait (§23.0).
 * ⚠️ UNE FONCTION, PAS UNE CONSTANTE : figée à l'import, elle garderait le mot de la plateforme quel
 *    que soit le magasin lu ensuite. Prix, taille et gencode restent des gabarits courts : ce sont
 *    des formes de VALEUR, pas des noms de champ — le code, lui, est un champ NOMMÉ, comme la
 *    désignation, et porte donc le mot du magasin.
 */
export function neutralPlateValues(): Record<LabelFieldKey, string> {
  return {
    desig: fieldLabel('name'), code: fieldLabel('articleCode'), price: '00,00', gencode: '0000000000000',
    // ⚠️ AUCUNE BARRE : une vignette ne montre pas un faux code à scanner, la trame grise dit « ici, le code-barres ».
    barcode: '',
    size: 'T', color: fieldLabel('color'), brand: fieldLabel('brand'), sup: fieldLabel('supplier'),
    origin: fieldLabel('originCountry'), season: fieldLabel('season'),
  };
}

/**
 * LA PLANCHE A4 ENTAMÉE — combien de cases de la première feuille ont déjà servi ; elles sont sautées à l'impression.
 * ⚠️ PARTAGÉE avec la fenêtre de Réceptions : une planche se gâche de la même façon quelle que soit la porte d'impression.
 */
export function UsedOnFirstSheetField({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <label style={{ minWidth: 130 }}>
      <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 4 }}>{t('Planche entamée')}</span>
      <input
        type="number"
        min={0}
        inputMode="numeric"
        aria-label={t('Étiquettes déjà utilisées sur la première feuille')}
        value={String(value)}
        onChange={(e) => onChange(Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
        style={{ width: '100%', height: 36, padding: '0 10px', textAlign: 'right', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}
      />
    </label>
  );
}

/**
 * LE CODE-BARRES TROP ÉTROIT, DIT AU MOMENT D'IMPRIMER — rien quand le gabarit n'en porte pas ou qu'il se scanne.
 * ⚠️ C'EST LÀ QU'IL COÛTE DU PAPIER : le taire ferait tirer une planche entière d'étiquettes illisibles, quelle que soit
 *    la porte d'impression (impression de masse ou Réceptions).
 */
export function BarcodeWidthWarning({ template }: { template: LabelTemplate }) {
  const q = templateBarcodeQuality(template);
  if (!q || q.scannable) return null;
  return (
    <span style={{ padding: '7px 10px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', border: '1px solid var(--amber)', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', maxWidth: 280, lineHeight: 1.4 }}>
      <Trans k='Code-barres à <1>{pct} %</1> de la taille nominale (minimum 80 %) — il risque de ne pas être scanné.' vars={{ pct: q.pct }} parts={[(x) => <strong>{x}</strong>]} /></span>
  );
}

/** La barre des trois sources. */
function SourceBar({ value, onChange }: { value: Source; onChange: (s: Source) => void }) {
  return (
    <Segmented
      label={t('Source des articles')}
      options={SOURCES.map((s) => ({ id: s.id, label: t(s.label) }))}
      value={value}
      onChange={(v) => onChange(v as Source)}
      size="sm"
    />
  );
}

/**
 * Le repère d'un temps du tunnel.
 * ⚠️ LES CHIFFRES CERCLÉS ①②③ VIENNENT DE LA MAQUETTE, et ils travaillent : ils disent qu'il y a un
 * ORDRE, ce qu'une pastille dessinée en CSS dit moins bien et qu'un simple titre ne dit pas du tout.
 */
function StepTitle({ mark, title }: { mark: string; title: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 7, flexShrink: 0 }}>
      <span style={{ fontSize: 'var(--t-h4)', color: 'var(--teal-600)' }}>{mark}</span>
      <span style={{ fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
    </span>
  );
}

/**
 * Un filtre de référentiel.
 * ⚠️ LE LIBELLÉ « TOUT » EST ACCORDÉ (« Toutes les marques », « Tous les secteurs ») : c'est le
 * mot de la maquette, et il porte l'information — « Tous » seul ne dit pas de quoi il parle une fois
 * les quatre listes côte à côte.
 */
function FacetPicker({ facet, label, all, value, options, onChange }: {
  facet: FacetKey; label: string; all: string; value: string; options: string[]; onChange: (v: string) => void;
}) {
  return (
    <Picker
      // ⚠️ L'identifiant DOM vient de la CLÉ, jamais du libellé : le mot appartient au magasin — un id
      //    qui en dériverait changerait quand le commerçant renomme, et deux champs surchargés vers le
      //    même mot donneraient deux ids identiques.
      id={`etq-facette-${facet}`}
      label={label}
      allLabel={all}
      value={value}
      options={options.map((o) => ({ value: o, label: o }))}
      onChange={onChange}
    />
  );
}

/** Un article du panier, dépliable sur ses déclinaisons. */
function CartRow({ entry, qtyOf, excluded, onToggle, onRemove, onExclude, onQty }: {
  entry: CartEntry;
  qtyOf: (v: ArticleVariant) => number;
  excluded: Set<string>;
  onToggle: () => void;
  onRemove: () => void;
  onExclude: (gencode: string, included: boolean) => void;
  onQty: (gencode: string, n: number) => void;
}) {
  const total = entry.variants.reduce((n, v) => n + qtyOf(v), 0);
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
        <button type="button" aria-label={entry.open ? t('Replier « {name} »', { name: entry.model.name }) : t('Déplier « {name} »', { name: entry.model.name })} onClick={onToggle}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-600)', padding: 2 }}>
          <Icon name={entry.open ? 'chevronD' : 'chevronR'} size={14} />
        </button>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.model.name}
        </span>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
          {entry.variants.length === 0 ? t('aucun code-barres') : t('{n} étiquette', { n: total })}
        </span>
        <button type="button" aria-label={t('Retirer « {name} » du panier', { name: entry.model.name })} title={t('Retirer du panier')} onClick={onRemove}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-500)', padding: 2 }}>
          <Icon name="x" size={13} />
        </button>
      </div>
      {entry.open && entry.variants.length > 0 && (
        <div style={{ borderTop: '1px solid var(--ink-100)', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entry.variants.map((v) => {
            const code = v.gencode!;
            const included = !excluded.has(code);
            return (
              <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-dense)' }}>
                <input type="checkbox" aria-label={t('Inclure {v} {v2} de « {name} »', { v: v.color ?? '', v2: v.size ?? '', name: entry.model.name })} checked={included}
                  onChange={(e) => onExclude(code, e.target.checked)} />
                <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-800)' }}>
                  {[v.color, v.size].filter(Boolean).join(' · ') || code}
                </span>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
                  {t('stock {v}', { v: v.stockQty ?? '—' })}
                </span>
                <input
                  type="number"
                  min={0}
                  aria-label={t('Étiquettes pour {v} {v2} de « {name} »', { v: v.color ?? '', v2: v.size ?? '', name: entry.model.name })}
                  value={String(qtyOf(v))}
                  disabled={!included}
                  onChange={(e) => onQty(code, Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
                  style={{ width: 62, height: 28, textAlign: 'right', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
